import React, { useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import {Translation as T} from "@awe-web/shared/lib";
import {Button} from "../../components/form.js";

import "./station_card.scss";

const OPENING_TIMES = "FSD0000106";
const NAVIGATE_TO_STATION_LINK = "FSD0000449";

export function StationAddress({ className, stationNameClassName, openingTimesHeadingClassName, station}) {
  const combinedClassName = classnames("station-address", className);
  const combinedStationClassName = classnames("station-address__name", stationNameClassName);
  
  const renderOpeningInfo = useCallback(() => {
    if (!Array.isArray(station?.openingInfos) || station.openingInfos.length === 0) {
      return null;
    }

    const combinedOpeningTimesClassName = classnames("station-address__opening-times-heading", openingTimesHeadingClassName);
    return (
      <>
        <div className={combinedOpeningTimesClassName}><T code={OPENING_TIMES} /></div>
        <ul className="station-address__opening-times">
          {station.openingInfos.map(({id, text}) => (<li key={id}>{text}</li>))}
        </ul>
      </>
    );
  }, [openingTimesHeadingClassName, station?.openingInfos]);

  return (
    <div className={combinedClassName}>
      <div className={combinedStationClassName}>{station?.shortdescr}</div>
      <div className="station-address__address">
        <div>{station?.address?.street}</div>
        <div>{station?.address?.zipCode} {station?.address?.city}</div>
      </div>
      {renderOpeningInfo()}
    </div>
  );
}

StationAddress.propTypes = {
  className: PropTypes.string,
  stationNameClassName: PropTypes.string,
  openingTimesHeadingClassName: PropTypes.string,
  station: PropTypes.shape({
    id: PropTypes.number,
    shortdescr: PropTypes.string,
    imageUrl: PropTypes.string,
    address: PropTypes.shape({
      street: PropTypes.string,
      zipCode: PropTypes.string,
      city: PropTypes.string
    }),
    openingInfos: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string
    }))
  })
};

function StationCard({station, onClick}) {
  const combinedClassName = classnames(
    "station-card",
    "station-card-list__station-card",
    {"station-card--clickable": typeof onClick === "function"}
  );

  const clickHandler = useCallback(() => {
    if (onClick) {
      onClick(station.id);
    }
  }, [onClick, station?.id]);

  const onMapClick = (event) => event.stopPropagation();

  return (
    <div className={combinedClassName} onClick={clickHandler}>
      <div className="station-card__image-wrapper">
        <div
          className="station-card__image-wrapper__image"
          style={{backgroundImage: `url(${station.imageUrl})`}}
        />
      </div>

      <div className="station-card__body">
        <StationAddress className="station-card__info" station={station} />

        <a href={station.googleMapsLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onMapClick}
          className="station-card__map-link"
        >
          <img className="station-card__map-link__map" src={station.mapUrl} alt="" />
          <Button nonInteractable tertiary className="station-card__map-link__text">
            <T code={NAVIGATE_TO_STATION_LINK} />
          </Button>
        </a>
      </div>
    </div>
  );
}

StationCard.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  station: StationAddress.propTypes.station
};

export function StationCardList({className, stations = [], onClick}) {
  const combinedClassName = classnames("station-card-list", className);

  return (
    <div className={combinedClassName}>
      {stations.map(station => (
        <StationCard
          key={station.id}
          onClick={onClick}
          station={station} />
      ))}
    </div>
  );
}

StationCardList.propTypes = {
  className: PropTypes.string,
  stations: PropTypes.arrayOf(StationCard.propTypes.station),
  onClick: StationCard.propTypes.onClick
};
